import React, { useState } from 'react';
import { Sparkles, Star, ArrowUp, Crown, Zap, Shield } from 'lucide-react';

// Accessory emoji positions & effects
const ACCESSORY_MAP = {
  hat_crown: { emoji: '👑', pos: 'top', offset: '-top-3' },
  hat_flower: { emoji: '🌸', pos: 'top', offset: '-top-2' },
  hat_wizard: { emoji: '🎩', pos: 'top', offset: '-top-4' },
  hat_party: { emoji: '🎉', pos: 'top', offset: '-top-3' },
  hat_beanie: { emoji: '🧢', pos: 'top', offset: '-top-3' },
  hat_sunhat: { emoji: '👒', pos: 'top', offset: '-top-3' },
  hat_snow: { emoji: '🎩', pos: 'top', offset: '-top-3' },
  bg_rainbow: { ring: 'rainbow', gradient: 'conic-gradient(#FF6B6B, #FFC8DD, #FDF0D5, #B9FBC0, #A2D2FF, #CDB4DB, #FF6B6B)' },
  bg_stars: { ring: 'glow', color: '#FDF0D5', glow: '0 0 20px rgba(255,215,0,0.5)' },
  bg_hearts: { ring: 'glow', color: '#FFC8DD', glow: '0 0 20px rgba(255,200,221,0.5)' },
  bg_nature: { ring: 'glow', color: '#B9FBC0', glow: '0 0 20px rgba(185,251,192,0.5)' },
  outfit_scarf: { emoji: '🧣', pos: 'bottom', offset: '-bottom-1' },
  outfit_cape: { emoji: '🦸', pos: 'bottom-right', offset: '-bottom-1 -right-1' },
  outfit_bow: { emoji: '🎀', pos: 'bottom-center', offset: '-bottom-2' },
  outfit_medal: { emoji: '🏅', pos: 'bottom-left', offset: '-bottom-1 -left-1' },
  outfit_raincoat: { emoji: '🧥', pos: 'bottom', offset: '-bottom-1' },
  outfit_umbrella: { emoji: '☂️', pos: 'bottom-right', offset: '-bottom-1 -right-1' },
  outfit_sunglasses: { emoji: '🕶️', pos: 'bottom-center', offset: '-bottom-2' },
  outfit_puffer: { emoji: '🧥', pos: 'bottom', offset: '-bottom-1' },
  outfit_tshirt: { emoji: '👕', pos: 'bottom-center', offset: '-bottom-2' },
  // Halloween 🎃
  hat_pumpkin: { emoji: '🎃', pos: 'top', offset: '-top-3' },
  hat_witch: { emoji: '🧙', pos: 'top', offset: '-top-4' },
  outfit_ghost: { emoji: '👻', pos: 'bottom-right', offset: '-bottom-1 -right-1' },
  bg_spooky: { ring: 'glow', color: '#6A0572', glow: '0 0 20px rgba(106,5,114,0.6)' },
  // Christmas 🎄
  hat_santa: { emoji: '🎅', pos: 'top', offset: '-top-3' },
  hat_antlers: { emoji: '🦌', pos: 'top', offset: '-top-4' },
  outfit_sweater: { emoji: '🎄', pos: 'bottom', offset: '-bottom-1' },
  bg_snowfall: { ring: 'glow', color: '#DCECFF', glow: '0 0 22px rgba(220,236,255,0.9)' },
  // Valentine 💘
  hat_heartcrown: { emoji: '💖', pos: 'top', offset: '-top-3' },
  outfit_rose: { emoji: '🌹', pos: 'bottom-center', offset: '-bottom-2' },
  outfit_lovetux: { emoji: '💘', pos: 'bottom-right', offset: '-bottom-1 -right-1' },
  bg_cupid: { ring: 'glow', color: '#FFC8DD', glow: '0 0 22px rgba(255,79,129,0.6)' },
  // Spring 🌸
  hat_bunny: { emoji: '🐰', pos: 'top', offset: '-top-3' },
  hat_petals: { emoji: '🌼', pos: 'top', offset: '-top-3' },
  outfit_butterfly: { emoji: '🦋', pos: 'bottom-right', offset: '-bottom-1 -right-1' },
  bg_cherry: { ring: 'glow', color: '#FFC8DD', glow: '0 0 22px rgba(255,200,221,0.7)' },
  // Summer ☀️
  hat_palm: { emoji: '🌴', pos: 'top', offset: '-top-4' },
  outfit_floatie: { emoji: '🛟', pos: 'bottom', offset: '-bottom-1' },
  outfit_icecream: { emoji: '🍦', pos: 'bottom-center', offset: '-bottom-2' },
  bg_sunset: { ring: 'glow', color: '#FF8C42', glow: '0 0 22px rgba(255,140,66,0.7)' },
};

const STAGE_VISUALS = {
  0: { label: 'Egg', borderClass: 'border-gray-400', ringColor: '#D0D0D0', bgGlow: 'none' },
  1: { label: 'Baby', borderClass: 'border-pink-400', ringColor: '#FFC8DD', bgGlow: 'none' },
  2: { label: 'Teen', borderClass: 'border-yellow-400', ringColor: '#F0C040', bgGlow: '0 0 15px rgba(240,192,64,0.3)' },
  3: { label: 'Adult', borderClass: 'border-blue-400', ringColor: '#A2D2FF', bgGlow: '0 0 20px rgba(162,210,255,0.4)' },
  4: { label: 'Legendary', borderClass: 'border-purple-500', ringColor: '#CDB4DB', bgGlow: '0 0 30px rgba(205,180,219,0.6), 0 0 60px rgba(255,200,221,0.3)' },
};

// Weather-reactive aura around pet avatar
function WeatherAura({ condition, size }) {
  if (!condition) return null;
  const dims = size === 'sm' ? { w: 64, h: 64 } : size === 'lg' ? { w: 144, h: 144 } : { w: 112, h: 112 };
  const particles = [];

  if (condition === 'rain' || condition === 'thunder' || condition === 'sleet') {
    for (let i = 0; i < 7; i++) {
      particles.push(
        <span
          key={`r${i}`}
          className="weather-particle weather-rain"
          style={{
            left: `${10 + i * 12}%`,
            top: `-6px`,
            animationDelay: `${i * 0.18}s`,
          }}
        >
          💧
        </span>
      );
    }
    if (condition === 'thunder') {
      particles.push(
        <span
          key="lightning"
          className="weather-particle weather-thunder"
          style={{ right: '10%', top: '20%', fontSize: '18px', textShadow: '0 0 8px #FFF176' }}
        >
          ⚡
        </span>
      );
    }
  } else if (condition === 'snow') {
    for (let i = 0; i < 8; i++) {
      particles.push(
        <span
          key={`s${i}`}
          className="weather-particle weather-snow"
          style={{
            left: `${8 + i * 11}%`,
            top: `-6px`,
            animationDelay: `${i * 0.25}s`,
          }}
        >
          ❄️
        </span>
      );
    }
  } else if (condition === 'clear') {
    // Sun rays around + sparkles
    particles.push(
      <span
        key="sun"
        className="weather-particle weather-sun"
        style={{
          right: '-8px',
          top: '-8px',
          fontSize: size === 'lg' ? '22px' : '18px',
          filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.7))',
        }}
      >
        ☀️
      </span>
    );
    for (let i = 0; i < 4; i++) {
      particles.push(
        <span
          key={`sp${i}`}
          className="weather-particle weather-sparkle"
          style={{
            left: `${20 + i * 18}%`,
            bottom: `-2px`,
            animationDelay: `${i * 0.45}s`,
            color: '#FFD700',
          }}
        >
          ✨
        </span>
      );
    }
  } else if (condition === 'partly_cloudy' || condition === 'cloudy' || condition === 'fog') {
    particles.push(
      <span
        key="cloud-l"
        className="weather-particle weather-cloud"
        style={{ left: '-10px', top: '-4px' }}
      >
        ☁️
      </span>
    );
    particles.push(
      <span
        key="cloud-r"
        className="weather-particle weather-cloud"
        style={{ right: '-10px', top: '8px', animationDelay: '1.2s', fontSize: '12px' }}
      >
        ☁️
      </span>
    );
  }

  return (
    <div
      className="absolute inset-0 z-40 pointer-events-none overflow-visible"
      style={{ width: dims.w, height: dims.h }}
      data-testid="pet-weather-aura"
    >
      {particles}
    </div>
  );
}

export function PetAvatar({ pet, size = 'md', weather = null }) {
  const stage = pet?.evolution_stage ?? 0;
  const visual = STAGE_VISUALS[stage] || STAGE_VISUALS[0];
  
  const sizes = {
    sm: { container: 'w-16 h-16', level: 'w-7 h-7 text-xs' },
    md: { container: 'w-28 h-28', level: 'w-9 h-9 text-sm' },
    lg: { container: 'w-36 h-36', level: 'w-10 h-10 text-sm' },
  };
  const s = sizes[size] || sizes.md;

  // Egg stage - show animated egg
  if (stage === 0 || !pet?.image) {
    const eggColor = pet?.egg_color || '#FDF0D5';
    return (
      <div className="relative inline-block">
        <div
          className={`${s.container} rounded-full border-4 ${visual.borderClass} flex items-center justify-center overflow-hidden`}
          style={{ 
            backgroundColor: eggColor,
            boxShadow: `4px 4px 0px 0px rgba(0,0,0,1)`,
          }}
        >
          <div className="text-center">
            <div className="text-3xl mb-1">🥚</div>
            <p className="text-[10px] font-bold">Hatching...</p>
          </div>
        </div>
        <div className={`absolute -bottom-1 -right-1 ${s.level} rounded-full bg-[#FDF0D5] border-2 border-black flex items-center justify-center font-bold font-['Fredoka']`}>
          {pet?.level || 1}
        </div>
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      {/* Live weather aura */}
      <WeatherAura condition={weather} size={size} />
      {/* Background ring from accessories */}
      {pet?.accessories?.background && ACCESSORY_MAP[pet.accessories.background]?.ring && (
        <div
          className="absolute -inset-2 rounded-full z-0"
          style={{
            background: ACCESSORY_MAP[pet.accessories.background].gradient || ACCESSORY_MAP[pet.accessories.background].color,
            boxShadow: ACCESSORY_MAP[pet.accessories.background].glow || 'none',
            opacity: 0.7,
          }}
        />
      )}
    
      {/* Legendary glow ring */}
      {stage === 4 && (
        <div 
          className="absolute inset-0 rounded-full animate-pulse-soft" 
          style={{
            boxShadow: visual.bgGlow,
            zIndex: 0,
          }}
        />
      )}
      
      <div
        className={`${s.container} rounded-full border-4 ${visual.borderClass} overflow-hidden relative z-10 ${stage >= 3 ? 'animate-pulse-soft' : ''}`}
        style={{ 
          boxShadow: `4px 4px 0px 0px rgba(0,0,0,1)${stage >= 3 ? ', ' + visual.bgGlow : ''}`,
        }}
      >
        <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" />
        
        {/* Sparkle overlay for legendary */}
        {stage === 4 && (
          <div className="absolute inset-0 pointer-events-none">
            <Sparkles className="absolute top-1 right-1 w-4 h-4 text-yellow-300 animate-pulse" />
            <Star className="absolute bottom-2 left-1 w-3 h-3 text-yellow-300 animate-pulse" fill="currentColor" />
          </div>
        )}
        
        {/* Crown for adult+ (only if no hat accessory) */}
        {stage >= 3 && !pet?.accessories?.hat && (
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20">
            <Crown className="w-5 h-5 text-yellow-500" fill="currentColor" />
          </div>
        )}
      </div>

      {/* Hat accessory emoji */}
      {pet?.accessories?.hat && ACCESSORY_MAP[pet.accessories.hat]?.emoji && (
        <div className={`absolute left-1/2 -translate-x-1/2 ${ACCESSORY_MAP[pet.accessories.hat].offset} z-30 text-2xl drop-shadow-md`}>
          {ACCESSORY_MAP[pet.accessories.hat].emoji}
        </div>
      )}

      {/* Outfit accessory emoji */}
      {pet?.accessories?.outfit && ACCESSORY_MAP[pet.accessories.outfit]?.emoji && (
        <div className={`absolute z-30 text-xl drop-shadow-md ${
          ACCESSORY_MAP[pet.accessories.outfit].pos === 'bottom-center' 
            ? 'left-1/2 -translate-x-1/2 -bottom-2'
            : ACCESSORY_MAP[pet.accessories.outfit].pos === 'bottom-right'
            ? '-bottom-1 -right-1'
            : ACCESSORY_MAP[pet.accessories.outfit].pos === 'bottom-left'
            ? '-bottom-1 -left-1'
            : 'left-1/2 -translate-x-1/2 -bottom-1'
        }`}>
          {ACCESSORY_MAP[pet.accessories.outfit].emoji}
        </div>
      )}
      
      {/* Level badge */}
      <div className={`absolute -bottom-1 -right-1 ${s.level} rounded-full bg-[#FDF0D5] border-2 border-black flex items-center justify-center font-bold font-['Fredoka'] z-20`}>
        {pet?.level || 1}
      </div>
      
      {/* Evolution ring color indicator */}
      {stage >= 2 && (
        <div
          className="absolute -top-1 -left-1 w-5 h-5 rounded-full border-2 border-black flex items-center justify-center z-20"
          style={{ backgroundColor: visual.ringColor }}
        >
          <Star className="w-3 h-3" fill="currentColor" />
        </div>
      )}
    </div>
  );
}

export function EvolveButton({ pet, onEvolve }) {
  const [hovering, setHovering] = useState(false);
  
  if (!pet?.can_evolve) return null;

  return (
    <button
      onClick={onEvolve}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="relative w-full neo-btn py-3 bg-gradient-to-r from-[#CDB4DB] via-[#FFC8DD] to-[#A2D2FF] text-black flex items-center justify-center gap-2 overflow-hidden"
      data-testid="evolve-btn"
      style={{
        animation: 'glow-pulse 2s ease-in-out infinite',
      }}
    >
      {/* Animated sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 6 }).map((_, i) => (
          <Sparkles
            key={i}
            className="absolute text-yellow-400 animate-pulse"
            style={{
              width: 14,
              height: 14,
              top: `${20 + Math.random() * 60}%`,
              left: `${10 + (i * 15)}%`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>
      
      <ArrowUp className="w-5 h-5 relative z-10" />
      <span className="font-bold text-lg relative z-10">
        Evolve {pet.name}!
      </span>
      <Sparkles className="w-5 h-5 relative z-10" />
      
      <style>{`
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 3px 3px 0px 0px rgba(0,0,0,1), 0 0 20px rgba(205,180,219,0.4); }
          50% { box-shadow: 3px 3px 0px 0px rgba(0,0,0,1), 0 0 40px rgba(255,200,221,0.6); }
        }
      `}</style>
    </button>
  );
}

export function EvolutionCeremony({ pet, evolutionResult, onClose }) {
  const [step, setStep] = useState(0); // 0=reveal, 1=info, 2=done

  if (!evolutionResult) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="evolution-ceremony">
      {/* Confetti particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[1001]">
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-sm"
            style={{
              width: `${6 + Math.random() * 14}px`,
              height: `${6 + Math.random() * 14}px`,
              background: ['#A2D2FF', '#FFC8DD', '#B9FBC0', '#CDB4DB', '#FDF0D5', '#FFD700', '#FF6B6B'][i % 7],
              left: `${Math.random() * 100}%`,
              top: `-20px`,
              animation: `evo-confetti ${2 + Math.random() * 3}s ease-in forwards`,
              animationDelay: `${Math.random() * 1.5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-[1002] max-w-md w-full mx-4 text-center animate-bounce-in">
        {/* Card */}
        <div className="neo-card p-8 bg-[#FFFAF0]">
          {step === 0 && (
            <>
              <div className="mb-4">
                <Sparkles className="w-8 h-8 mx-auto text-yellow-500 animate-pulse" />
              </div>
              <h1 className="text-4xl font-bold font-['Fredoka'] mb-4 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 bg-clip-text text-transparent">
                EVOLUTION!
              </h1>
              
              {/* New pet image with glow */}
              <div className="relative inline-block mb-4">
                <div
                  className="w-40 h-40 rounded-full border-4 border-yellow-400 overflow-hidden mx-auto"
                  style={{
                    boxShadow: '0 0 40px rgba(255,215,0,0.5), 0 0 80px rgba(205,180,219,0.3), 4px 4px 0px 0px rgba(0,0,0,1)',
                  }}
                >
                  {evolutionResult.image ? (
                    <img src={evolutionResult.image} alt="Evolved pet" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#FDF0D5] flex items-center justify-center text-5xl">🥚</div>
                  )}
                </div>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Crown className="w-8 h-8 text-yellow-500" fill="currentColor" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold font-['Fredoka'] mb-2">
                {pet?.name} evolved into
              </h2>
              <h3 className="text-3xl font-bold font-['Fredoka'] text-purple-600 mb-4">
                {evolutionResult.stage_title}
              </h3>
              
              <button
                onClick={() => setStep(1)}
                className="neo-btn px-8 py-3 bg-[#B9FBC0]"
                data-testid="evolution-next-btn"
              >
                <span className="font-bold text-lg">See Perks</span>
              </button>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="text-2xl font-bold font-['Fredoka'] mb-6">New Perks Unlocked!</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-[#B9FBC0] rounded-xl border-2 border-black">
                  <Shield className="w-6 h-6" />
                  <div className="text-left">
                    <p className="font-bold">Reduced Decay</p>
                    <p className="text-sm text-gray-600">
                      {evolutionResult.perk_decay === 0 
                        ? 'No health/happiness decay!' 
                        : `${((1 - evolutionResult.perk_decay) * 100).toFixed(0)}% less health & happiness decay`
                      }
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-[#FDF0D5] rounded-xl border-2 border-black">
                  <Zap className="w-6 h-6" />
                  <div className="text-left">
                    <p className="font-bold">Bonus Points</p>
                    <p className="text-sm text-gray-600">
                      +{evolutionResult.perk_bonus} bonus points per completed task
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-[#CDB4DB] rounded-xl border-2 border-black">
                  <Star className="w-6 h-6" fill="currentColor" />
                  <div className="text-left">
                    <p className="font-bold">{evolutionResult.border_style} Border</p>
                    <p className="text-sm text-gray-600">
                      New visual flair for your companion!
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="neo-btn px-8 py-3 bg-[#A2D2FF] w-full"
                data-testid="evolution-close-btn"
              >
                <span className="font-bold text-lg">Awesome!</span>
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes evo-confetti {
          0% { top: -20px; opacity: 1; transform: rotate(0deg) translateX(0); }
          100% { top: 110vh; opacity: 0; transform: rotate(1080deg) translateX(${Math.random() > 0.5 ? '' : '-'}100px); }
        }
      `}</style>
    </div>
  );
}

export function StageIndicator({ pet }) {
  const stage = pet?.evolution_stage ?? 0;
  const stages = ['Egg', 'Baby', 'Teen', 'Adult', 'Legendary'];
  const colors = ['#D0D0D0', '#FFC8DD', '#F0C040', '#A2D2FF', '#CDB4DB'];

  return (
    <div className="flex items-center gap-1 mt-2" data-testid="stage-indicator">
      {stages.map((s, i) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-4 h-4 rounded-full border-2 ${i <= stage ? 'border-black' : 'border-gray-300'} transition-all`}
            style={{ backgroundColor: i <= stage ? colors[i] : '#f0f0f0' }}
            title={s}
          />
          {i < 4 && (
            <div className={`w-3 h-0.5 ${i < stage ? 'bg-black' : 'bg-gray-300'}`} />
          )}
        </div>
      ))}
      <span className="ml-1 text-xs font-bold text-gray-500">{stages[stage]}</span>
    </div>
  );
}
